import { defineStore } from 'pinia';
import { paymentLinkApi } from '../paymentLinkApi.js';

/**
 * Payment link store — backed by the new payment-link API:
 *   POST   /api/payment-links                        create
 *   GET    /api/payment-links/{path}/{slug}          preview (public)
 *   POST   /api/payment-links/{path}/{slug}/checkout
 *
 * `path` is the URL bucket: "p" | "c" | "i".
 * The legacy list/update/delete endpoints no longer exist; link lifecycle
 * (expiry, revocation, usage limits) is enforced server-side.
 */
export const usePaymentLinkStore = defineStore('paymentLink', {
  state: () => ({
    /** Result of the last created link (PaymentLinkCreatedResponse). */
    createdLink: null,
    /** Preview payload for the link currently being viewed. */
    preview: null,
    /** Active checkout session (CheckoutSessionResponse). */
    checkoutSession: null,
    loading: false,
    error: null,
  }),

  actions: {
    /**
     * Create a payment link.
     * @param {object} payload CreatePaymentLinkRequest
     * @returns {Promise<object>} PaymentLinkCreatedResponse
     */
    async createPaymentLink(payload) {
      this.loading = true;
      this.error = null;
      try {
        this.createdLink = await paymentLinkApi.create(payload);
        return this.createdLink;
      } catch (err) {
        this.error = err;
        throw err;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Public preview of a payment link by path bucket + slug.
     * @param {string} path one of "p" | "c" | "i"
     * @param {string} slug
     */
    async previewPaymentLink(path, slug) {
      this.loading = true;
      this.error = null;
      try {
        this.preview = await paymentLinkApi.preview(path, slug);
        return this.preview;
      } catch (err) {
        this.error = err;
        throw err;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Create a server-side checkout session for a link.
     * @param {string} path one of "p" | "c" | "i"
     * @param {string} slug
     * @param {object} payload CreateCheckoutRequest
     */
    async checkoutPaymentLink(path, slug, payload) {
      this.loading = true;
      this.error = null;
      try {
        this.checkoutSession = await paymentLinkApi.checkout(path, slug, payload);
        return this.checkoutSession;
      } catch (err) {
        this.error = err;
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
});
